import { memo, useCallback, useEffect, useState } from 'react';

import './VerifyEmailModal.css';
import { useTranslation } from 'react-i18next';
import {
  GeneralModal,
  GeneralModalNoButtonVariant,
  GeneralModalYesButtonVariant,
} from '../GeneralModal';
import { SvgIcon } from '../../SVGIcon';
import { SVGIconType } from '../../../../constants/svgIcon';

type VerifyEmailModalProps = {
  email: string;
  onYes: () => void;
  onClose: () => void;
  isWEA?: boolean;
  isSent?: boolean;
  setIsSent: () => void;
};

export const VerifyEmailModal = memo(
  ({
    email,
    onYes,
    onClose,
    isWEA,
    isSent,
    setIsSent,
  }: VerifyEmailModalProps) => {
    const { t } = useTranslation();
    const [countdown, setCountdown] = useState(60);
    const [isCountdownActive, setIsCountdownActive] = useState(false);
    const [isFirstSent, setIsFirstSent] = useState(false);

    useEffect(() => {
      let timer: NodeJS.Timeout;
      if (isCountdownActive && countdown > 0) {
        timer = setInterval(() => {
          setCountdown((prev) => prev - 1);
        }, 1000);
      } else if (countdown === 0) {
        setIsCountdownActive(false);
        setCountdown(60);
        setIsSent();
      }
      return () => clearInterval(timer);
    }, [countdown, isCountdownActive, setIsSent]);

    useEffect(() => {
      if (isSent) {
        setCountdown(60);
        setIsCountdownActive(true);
        setIsFirstSent(true);
      }
    }, [isSent]);

    // const handleSendClick = useCallback(() => {
    //   onYes();
    //   setIsEmailSent(true);
    //   setIsCountdownActive(true);
    // }, [onYes]);

    const handleResendClick = useCallback(() => {
      if (!isSent) {
        onYes();
      }
    }, [isSent, onYes]);

    return (
      <GeneralModal
        isYesNo={true}
        onYes={handleResendClick}
        onClose={onClose}
        buttonSetting={{
          noText: t('Cancel'),
          yesText: isSent
            ? isCountdownActive
              ? `${t('Resend')} ${countdown}s`
              : t('Resend')
            : t('Send'),
          noVariant: GeneralModalNoButtonVariant.Gray,
          yesVariant: isWEA
            ? isCountdownActive
              ? GeneralModalYesButtonVariant.WEAblueDisabled
              : GeneralModalYesButtonVariant.WEABlue
            : isCountdownActive
            ? GeneralModalYesButtonVariant.cyanDisabled
            : GeneralModalYesButtonVariant.Cyan,
        }}
      >
        <div className="verify-email-general">
          {!isFirstSent ? (
            <div className="centered-container">
              <div className="title">{t('Verify Email')}</div>
              <div className="email">{email}</div>
              <div className="sub-title">
                {t(
                  'Please verify this email before getting a code to reset password.',
                )}
              </div>
              <div className="content">
                {t('Click send and we will send you a verification email.')}
              </div>
            </div>
          ) : (
            <div className="centered-container">
              <div className="title-icon">
                <SvgIcon
                  name={SVGIconType.CircleTick2}
                  fill="white"
                  stroke="black"
                  color="black"
                  width={100}
                  height={100}
                />
              </div>
              <div className="title">{t('Verification Email Sent')}</div>
              <div className="email">{email}</div>
              <div className="sub-title">
                {t('We have just sent you a verification email.')}
                <br />
                {t('Please check your inbox.')}
              </div>
              <div className="content">
                {t('Please also check your spam folder / junk box.')}
              </div>
            </div>
          )}
        </div>
      </GeneralModal>
    );
  },
);
